import {
  EnvironmentProviders,
  forwardRef,
  Injectable,
  makeEnvironmentProviders,
  Provider,
  inject,
} from '@angular/core';
import { HTTP_INTERCEPTORS, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { first, Observable, retry, throwError, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { AuthService } from 'shared';
import { AuthenticationConfig } from 'shared';
import {
  ACCESS_TOKEN_INTERCEPTOR_CONFIG,
  AccessTokenInterceptorConfig,
} from './interceptor-token/interceptor-config.token';

export const ACCESS_TOKEN_API_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => AccessTokenApiInterceptor),
  multi: true,
};

export function provideAccessTokenInterceptor(config?: AccessTokenInterceptorConfig): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: ACCESS_TOKEN_INTERCEPTOR_CONFIG,
      useValue: config ?? { ignoreUrls: [] },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AccessTokenApiInterceptor,
      multi: true,
    },
  ]);
}

@Injectable({ providedIn: 'root' })
export class AccessTokenApiInterceptor implements HttpInterceptor {
  private authenticationConfig = inject(AuthenticationConfig);
  private authService = inject(AuthService);
  private interceptorConfig = inject<AccessTokenInterceptorConfig>(ACCESS_TOKEN_INTERCEPTOR_CONFIG);

  private IGNORE_URLS = [
    'assets/i18n',
    'password/reset',
    this.authenticationConfig.keycloakUrl,
    ...(this.interceptorConfig.ignoreUrls || []),
  ];

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.IGNORE_URLS.some((ignoreUrl) => request.url.includes(ignoreUrl))) {
      return next.handle(request);
    }

    return this.authService.getToken().pipe(
      first(),
      mergeMap((accessToken) =>
        next.handle(this.injectToken(request, accessToken)).pipe(
          retry({
            count: this.authenticationConfig.enableRequestRetry ? 5 : 0,
            delay: (err, retryCount) => {
              switch (err.status) {
                case 401:
                case 403:
                case 404:
                case 500:
                  return throwError(() => err);
                default:
                  return timer(1000 * (retryCount * retryCount));
              }
            },
          }),
        ),
      ),
    );
  }
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  public injectToken(request: HttpRequest<any>, jwt: string) {
    return request.clone({ setHeaders: { Authorization: `Bearer ${jwt}` } });
  }
}
